import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { Button, FlexBox, Spinner, Typography } from '@vp/swan';
import { ValidationState } from '@vp/upload-and-print-validator';
import { useErrorContext } from 'src/hooks/ErrorContextHooks';
import { useProductContext } from 'src/hooks/ProductContextHooks';
import { useStudioContext } from 'src/hooks/StudioContextHooks';
import { useProcessUpload, useUploadContext } from 'src/hooks/UploadContextHooks';
import { useTrackingEvent } from 'src/hooks/useTrackingEvent';
import { useValidationContext } from 'src/hooks/ValidationContextHooks';
import { usePromptContext } from 'src/providers/PromptContextProvider';
import { createWork } from 'src/services/UploadAndPrintService';
import { UPLOAD_UNIFIED_EVENT, UploadSteps } from 'src/utils/Constants';
import { getLanguage, text } from 'src/utils/localizationUtil';
import QueryStringUtil from 'src/utils/QueryStringUtil';
import { hasValidationErrors } from 'src/utils/validationUtil';
import { ReviewGuidelinesButton } from './ReviewGuidelinesButton';
import { SaveChangeButton } from './SaveChangeButton';
import { AnalyticsEventData } from '../Analytics';

interface FooterButtonProps {
  /** Flag indicating if component is rendered on mobile viewport */
  isMobile: boolean;
  /** Flag controlling visibility of error card elements */
  showErrorCards: boolean;
}

export const FooterButton = ({ isMobile, showErrorCards }: FooterButtonProps) => {
  const { setError } = useErrorContext();
  const { reprocessUpload } = useProcessUpload();
  const { upload, setUpload } = useUploadContext();
  const { isStudioInitialized, studioUrl } = useStudioContext();
  const { isValidating, validations } = useValidationContext();
  const { productInformation } = useProductContext();
  const { showValidationErrorPrompt, showProductOptionsPrompt } = usePromptContext();
  const { sendSegmentTrackingEvent } = useTrackingEvent();
  const [orderSubmitting, setOrderSubmitting] = useState(false);

  const lng = getLanguage();

  const hasUnValidatedValidations = useMemo(() => {
    return validations?.some(validation => validation.state === ValidationState.UnValidated) ?? false;
  }, [validations]);

  /**
   * Determines if the form is valid based on the current upload state and validation status.
   *
   * The form is considered valid if:
   * - The upload has a `processedUploadUri` and there are no active validations or validation errors
   * - The upload has a `fileUri` and `hasUnValidatedValidations` is true
   *
   * This function is memoized using `useCallback` to avoid unnecessary re-computations.
   */
  const formIsValid = useCallback(() => {
    if (!upload) {
      return false;
    }
    const hasValidProcessedUpload = upload.processedUploadUri && !isValidating && !hasValidationErrors(validations);
    const isPendingRevalidation = upload.fileUri && hasUnValidatedValidations;

    return hasValidProcessedUpload || isPendingRevalidation;
  }, [upload, isValidating, validations, hasUnValidatedValidations]);

  const handleReprocessUpload = useCallback(async () => {
    const reprocessResult = reprocessUpload();

    if (!reprocessResult) {
      throw new Error('Error reprocessing upload');
    }

    const awaitedResult = await reprocessResult;

    if (!awaitedResult) {
      throw new Error('Error awaiting results from the reprocessed upload');
    }

    const { newUpload, processUploadResponse } = awaitedResult;

    if (!newUpload.processedUploadUri) {
      throw new Error('Reprocessed upload is missing a processedUploadUri');
    }

    return { newUpload, processUploadResponse };
  }, [reprocessUpload]);

  const submitForm = useCallback(
    async (customizeDesign: boolean) => {
      try {
        if (!upload || (!upload.processedUploadUri && !hasUnValidatedValidations)) {
          setError(new Error('Tried to checkout without an upload or without a processedUploadUri'));
          return;
        }

        setOrderSubmitting(true);

        let uploadToUse = upload;

        if (!customizeDesign) {
          let hasRevalidationErrors = false;

          // Revalidate if we don't have validations or something has not been validated.
          // This is typically caused by a page refresh, or by a customer changing their product option selection
          // after validations have run
          if (
            !validations ||
            validations.filter(v => v.state === ValidationState.UnValidated).length > 0 ||
            hasUnValidatedValidations
          ) {
            const { newUpload, processUploadResponse } = await handleReprocessUpload();

            if (!newUpload.processedUploadUri) {
              setError(new Error('Reprocessed upload is missing a processedUploadUri'));
              return;
            }

            uploadToUse = newUpload;

            // The formIsValid check won't catch any errors that occur during revalidation,
            // because setState calls don't modify the state until the next render, so
            // the formIsValid check will be looking at the old validation state
            processUploadResponse.validations.forEach(validation => {
              if (validation.state === ValidationState.Error || validation.state === ValidationState.Warning) {
                hasRevalidationErrors = true;
              }
            });
          }

          if (!formIsValid() || hasRevalidationErrors) {
            setOrderSubmitting(false);
            return;
          }
        }

        if (!uploadToUse.processedUploadUri) {
          setError(new Error('Upload is missing a processedUploadUri'));
          return;
        }

        const editUrl = QueryStringUtil.getValueByKey('editUrl') || buildEditUrl(studioUrl);
        const workId = QueryStringUtil.getValueByKey('workId');
        const createdBy = QueryStringUtil.getValueByKey('createdBy');
        const vistaCreateProjectId = QueryStringUtil.getValueByKey('vistaCreateProjectId');

        // Add to my projects
        const { nextPageUrl, workId: newWorkId } = await createWork(
          uploadToUse.processedUploadUri,
          productInformation.productName,
          productInformation.productKey,
          productInformation.productVersion,
          productInformation.quantity,
          lng,
          workId,
          productInformation.selectedOptions,
          editUrl,
          createdBy,
          vistaCreateProjectId
        );

        // Clear upload once we are ready to redirect customer
        setUpload(null);

        // Send user to the next page in the flow
        window.location.href = customizeDesign && editUrl ? editUrl.replace(`\${workId}`, newWorkId) : nextPageUrl;
      } catch (e) {
        console.error('Error submitting form', e);
        setError(e);
      } finally {
        setOrderSubmitting(false);
      }
    },
    [
      upload,
      hasUnValidatedValidations,
      studioUrl,
      productInformation.productName,
      productInformation.productKey,
      productInformation.productVersion,
      productInformation.quantity,
      productInformation.selectedOptions,
      lng,
      setUpload,
      setError,
      validations,
      formIsValid,
      handleReprocessUpload,
    ]
  );

  /**
   * Renders the footer content for the UploadAndPrint component.
   * The footer can display a "Save Changes" button, a "Review Guidelines" button, or a "Next" button with a spinner and text.
   * The footer also includes a mobile-specific design review permission note.
   *
   * @returns {JSX.Element} The rendered footer content
   */
  const handleReviewGuidelineClick = useCallback(() => {
    sendSegmentTrackingEvent({
      action: UPLOAD_UNIFIED_EVENT,
      uploadStep: UploadSteps.SpecsTemplatesViewed,
      label: 'Footer Button',
      category: 'Design - Upload and Print',
    } as AnalyticsEventData);
  }, [sendSegmentTrackingEvent]);

  const handleSaveClick = useCallback(() => {
    sendSegmentTrackingEvent({
      action: UPLOAD_UNIFIED_EVENT,
      label: 'Confirm Adjustment - Footer',
      category: 'Design - Upload and Print',
    } as AnalyticsEventData);
  }, [sendSegmentTrackingEvent]);

  const handleNextClick = useCallback(
    (event: React.FormEvent) => {
      sendSegmentTrackingEvent({
        action: UPLOAD_UNIFIED_EVENT,
        label: 'Next',
        category: 'Design - Upload and Print',
      } as AnalyticsEventData);
      event.preventDefault();
      submitForm(false);
    },
    [sendSegmentTrackingEvent, submitForm]
  );

  const renderFooter = useCallback(() => {
    const hasPromptsOpened = showValidationErrorPrompt || showProductOptionsPrompt;

    if (!hasPromptsOpened) {
      if (productInformation.recommendedProductData) {
        return <SaveChangeButton showFullWidth={true} onSave={handleSaveClick} />;
      }

      if (showErrorCards) {
        return <ReviewGuidelinesButton showFullWidth={true} onClickReviewGuidelines={handleReviewGuidelineClick} />;
      }
    }

    return (
      <>
        <Button
          skin="primary"
          size="standard"
          width="full-width"
          iconPosition="right"
          onClick={handleNextClick}
          disabled={!formIsValid() || orderSubmitting || !isStudioInitialized}
        >
          {orderSubmitting && <Spinner accessibleText={''} />}
          {orderSubmitting ? text('processing') : text('next-button')}
        </Button>
        {isMobile ? (
          <Fragment />
        ) : (
          <Typography fontSkin="footnote" className="design-review-note">
            {text('design-review-permission')}
          </Typography>
        )}
      </>
    );
  }, [
    formIsValid,
    isMobile,
    isStudioInitialized,
    orderSubmitting,
    productInformation.recommendedProductData,
    showErrorCards,
    showProductOptionsPrompt,
    showValidationErrorPrompt,
    handleNextClick,
    handleReviewGuidelineClick,
    handleSaveClick,
  ]);

  return (
    <FlexBox
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      padding={isMobile ? 5 : 6}
      className="design-review-form-footer"
    >
      {renderFooter()}
    </FlexBox>
  );
};

function buildEditUrl(studioUrl: string | undefined): string | null {
  if (!studioUrl) {
    return null;
  }

  const path = studioUrl.substring(0, studioUrl.indexOf('?'));
  return `${path}?workId=\${workId}`;
}

FooterButton.displayName = 'FooterButton';
